// Funnel-measurement + internal-attribution contracts shared by the API, the admin web app and the
// public landing app (Block 7). Kept here so every surface names the same event types, attribution
// sources and date-range presets, and validates the visitor id against one canonical shape.
// ---------------------------------------------------------------------------

/**
 * Landing page funnel events the public beacon may send. Mirrors the Prisma `LandingEventType` enum. `PRINT_SCAN` (a
 * printed voucher QR scan) is only stored for VOUCHER campaigns with printed vouchers on, gated in the service. Every
 * reader filters by type, so scans never count as views. One row per (campaign, type, visitor) — the DB unique
 * constraint is the dedupe, so views = unique visitors.
 */
export const LANDING_EVENT_TYPES = ['PAGE_VIEW', 'FORM_START', 'PRINT_SCAN'] as const;
export type LandingEventType = (typeof LANDING_EVENT_TYPES)[number];

/**
 * How an appointment came to carry a campaign. Mirrors the Prisma `AppointmentAttributionSource`
 * enum. `AUTO_PHONE` = the engine matched the guest by phone inside the window; `STAFF` = a human
 * set (or cleared) it in the appointment panel — `STAFF` always wins and is never overwritten.
 */
export const APPOINTMENT_ATTRIBUTION_SOURCES = ['AUTO_PHONE', 'STAFF'] as const;
export type AppointmentAttributionSourceValue = (typeof APPOINTMENT_ATTRIBUTION_SOURCES)[number];

/** Human labels for the "Where this client came from" block on the appointment panel. */
export const APPOINTMENT_ATTRIBUTION_SOURCE_LABELS: Record<AppointmentAttributionSourceValue, string> = {
  AUTO_PHONE: 'Matched by phone',
  STAFF: 'Set by staff',
};

/**
 * Forward attribution window: a booking is linked to a submission whose `createdAt` is at most this many
 * days BEFORE the booking (sign up, then book within 30 days).
 */
export const ATTRIBUTION_WINDOW_DAYS = 30;

/**
 * Pre-sign-up grace: a booking made shortly BEFORE the sign-up still counts (the guest booked by phone,
 * then filled the form they were looking at). Anything booked earlier than this was not earned by the
 * campaign — the guest was already coming.
 */
export const ATTRIBUTION_PRE_SIGNUP_GRACE_HOURS = 2;

/** localStorage key holding the first-party visitor id the landing app mints (never PII). */
export const LANDING_VISITOR_ID_STORAGE_KEY = 'hv_vid';

/** The only accepted visitor-id shape — validated on both the client (re-mint if corrupt) and the API. */
export const LANDING_VISITOR_ID_PATTERN = /^[A-Za-z0-9_-]{16,64}$/;

/** Date-range presets shared by the Leads and Analytics surfaces (Phase 03 consumes these). */
export const ANALYTICS_RANGE_PRESETS = ['all', '7d', '30d', '90d'] as const;
export type AnalyticsRangePreset = (typeof ANALYTICS_RANGE_PRESETS)[number];
